import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import api from "../api";
import { useMasterData } from "../../utils/useMasterData";

const Page = styled.div`
  background: #f8fafc;
  min-height: 100vh;
  padding: 16px;
  color: #0f172a;
`;
const Title = styled.h2`
  margin: 0 0 8px;
  font-size: 1.6rem;
  font-weight: 800;
`;
const Desc = styled.p`
  margin: 0 0 12px;
  color: #64748b;
  font-size: 0.9rem;
`;
const Card = styled.div`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 10px 22px rgba(15, 23, 42, 0.08);
  padding: 14px;
`;
const Row = styled.div`
  display: grid;
  grid-template-columns: 120px 1fr auto auto;
  gap: 10px;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #f1f5f9;
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;
const Label = styled.label`
  display: block;
  margin-bottom: 6px;
  font-size: 0.8rem;
  color: #334155;
  font-weight: 700;
`;
const Select = styled.select`
  width: 100%;
  min-height: 44px;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  padding: 10px 12px;
  color: #0f172a;
  background: #fff;
`;
const Input = styled.input`
  width: 100%;
  min-height: 44px;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  padding: 10px 12px;
  color: #0f172a;
  background: #fff;
`;
const Btn = styled.button`
  min-height: 44px;
  border: none;
  border-radius: 10px;
  padding: 0 14px;
  font-weight: 700;
  cursor: pointer;
  background: ${(p) => (p.$secondary ? "#e2e8f0" : "#2563eb")};
  color: ${(p) => (p.$secondary ? "#0f172a" : "#fff")};
`;
const Toggle = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;
const Head = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 12px;
`;
const Inline = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
`;
const Info = styled.div`
  margin-top: 10px;
  color: #64748b;
  font-size: 0.85rem;
`;
const Toast = styled.div`
  margin-bottom: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid ${(p) => (p.$error ? "#fecaca" : "#bbf7d0")};
  background: ${(p) => (p.$error ? "#fef2f2" : "#f0fdf4")};
  color: ${(p) => (p.$error ? "#b91c1c" : "#166534")};
  font-weight: 700;
  font-size: 0.86rem;
`;

export default function QCChecklistBuilder() {
  const { getValues } = useMasterData("STAGE_CODE");
  const stageCodes = getValues("STAGE_CODE");
  const [stageCode, setStageCode] = useState("WAX_CREATION");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ text: "", error: false });

  const normalizedItems = useMemo(
    () =>
      items.map((item, idx) => ({
        id: item.id || idx + 1,
        text: String(item.text || item.item || ""),
        required: Boolean(item.required),
      })),
    [items]
  );

  const fetchChecklist = async (stage) => {
    setLoading(true);
    setToast({ text: "", error: false });
    try {
      // Preferred endpoint per requirement
      const res = await api.get("/qc/checklists", { params: { stage_code: stage } });
      setItems(res?.data?.items || []);
    } catch {
      try {
        // Backward compatibility fallback
        const fallback = await api.get(`/qc/checklists/${stage}`);
        setItems(fallback?.data?.items || []);
      } catch (err) {
        setItems([]);
        setToast({
          text: err?.response?.data?.message || "Failed to load checklist.",
          error: true,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChecklist(stageCode);
  }, [stageCode]);

  useEffect(() => {
    if (stageCodes.length && !stageCodes.includes(stageCode)) {
      setStageCode(stageCodes[0]);
    }
  }, [stageCode, stageCodes]);

  const addItem = () => {
    setItems((prev) => [...prev, { text: "", required: false }]);
  };

  const updateItem = (index, patch) => {
    setItems((prev) => prev.map((item, idx) => (idx === index ? { ...item, ...patch } : item)));
  };

  const removeItem = (index) => {
    setItems((prev) => prev.filter((_, idx) => idx !== index));
  };

  const saveChecklist = async () => {
    const payloadItems = normalizedItems
      .filter((item) => item.text.trim())
      .map((item, idx) => ({
        id: idx + 1,
        text: item.text.trim(),
        required: item.required,
      }));

    if (!payloadItems.length) {
      setToast({ text: "Please add at least one checklist item.", error: true });
      return;
    }

    setSaving(true);
    setToast({ text: "", error: false });
    try {
      // Preferred endpoint per requirement
      await api.post("/qc/checklists", {
        stage_code: stageCode,
        items: payloadItems,
      });
      setToast({ text: `Checklist saved for ${stageCode} stage`, error: false });
      await fetchChecklist(stageCode);
    } catch {
      try {
        // Backward compatibility fallback
        await api.post(`/qc/checklists/${stageCode}`, { items: payloadItems });
        setToast({ text: `Checklist saved for ${stageCode} stage`, error: false });
        await fetchChecklist(stageCode);
      } catch (err) {
        setToast({
          text: err?.response?.data?.message || "Failed to save checklist.",
          error: true,
        });
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Page>
      <Title>QC Checklist Configuration</Title>
      <Desc>Define required checklist items for each production stage.</Desc>
      {toast.text ? <Toast $error={toast.error}>{toast.text}</Toast> : null}

      <Card>
        <Head>
          <div style={{ minWidth: 280 }}>
            <Label>Stage Selector</Label>
            <Select value={stageCode} onChange={(e) => setStageCode(e.target.value)}>
              {stageCodes.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </Select>
          </div>
          <Inline>
            <Btn $secondary onClick={addItem}>
              + Add Item
            </Btn>
            <Btn onClick={saveChecklist} disabled={saving || loading}>
              {saving ? "Saving..." : "Save Checklist"}
            </Btn>
          </Inline>
        </Head>

        {loading ? (
          <Info>Loading checklist...</Info>
        ) : normalizedItems.length === 0 ? (
          <Info>No checklist items found for this stage.</Info>
        ) : (
          normalizedItems.map((item, idx) => (
            <Row key={`${stageCode}-${idx}`}>
              <div>
                <Label>Required</Label>
                <Toggle
                  type="checkbox"
                  checked={item.required}
                  onChange={(e) => updateItem(idx, { required: e.target.checked })}
                />
              </div>
              <div>
                <Label>Item Text</Label>
                <Input
                  value={item.text}
                  onChange={(e) => updateItem(idx, { text: e.target.value })}
                  placeholder="Enter checklist item"
                />
              </div>
              <div style={{ alignSelf: "end" }}>
                <Btn $secondary onClick={() => removeItem(idx)}>
                  Delete
                </Btn>
              </div>
              <div style={{ color: "#64748b", fontSize: "0.8rem", alignSelf: "end" }}>#{idx + 1}</div>
            </Row>
          ))
        )}
      </Card>
    </Page>
  );
}
